/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / STATS-WARENWIRTSCHAFT — Bereich B (S180 Auswertung-Rework, Cut 3)
   =============================================================================
   Füllt die Warenwirtschaft-Shell mit echten Kennzahlen. Daten frisch über RPCs
   (phase_137 + bestehende ERP-RPCs), NIE aus _state-Cache. Charts = inline-SVG/HTML
   (kein recharts), Dark-Mode über CSS-Tokens. Härtung: Promise.allSettled → eine
   fehlende Migration/RPC degradiert nur ihre Sektion.

   Aufbau (Handoff 3a–3e):
     3a stats_item_margins   → Deckungsbeitrag je Artikel + Ampel (food-cost.js-Parität).
     3b (abgeleitet)         → Wareneinsatz-Quote + Rohertrag (aus 3a / sales_gross).
     3c stats_purchasing     → Einkauf je Lieferant.
     3d stats_stock_flows    → Schwund + Zutaten-Verbrauch.
     3e stock_value_totals / lots_summary / stock_overview → Bestandswert + MHD;
        Renner-Penner-Matrix (Menge×DB aus 3a, Klassifizierung pure).

   Wareneinsatz/Marge EXAKT wie food-cost.js (Parität) — Helfer von dort importiert.
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { fmtCents } from './stats-tagesgeschaeft.js';
import { collapsibleRankHtml, collapsibleRowsHtml } from './stats-collapse.js';
import { ampelFor, ampelColor } from '../../core/food-cost.js';

/* -------------------------------------------------------------------------- */
/* Pure Helfer (Vitest)                                                        */
/* -------------------------------------------------------------------------- */

/** Wareneinsatz-Quote % = Wareneinsatz / Umsatz × 100 (1 NK). null wenn Umsatz≤0. */
export function wareneinsatzQuotePct(goodsCostCents, salesGrossCents) {
  const s = Number(salesGrossCents) || 0;
  if (s <= 0) return null;
  return Math.round((Number(goodsCostCents) || 0) / s * 1000) / 10;
}

/** Rohertrag (Cents) = Umsatz − Wareneinsatz. */
export function rohertragCents(salesGrossCents, goodsCostCents) {
  return (Number(salesGrossCents) || 0) - (Number(goodsCostCents) || 0);
}

/** Schwund-Quote % = Schwundwert / Wareneinsatz × 100 (1 NK). null wenn Wareneinsatz≤0. */
export function schwundQuotePct(wasteCents, goodsCostCents) {
  const g = Number(goodsCostCents) || 0;
  if (g <= 0) return null;
  return Math.round((Number(wasteCents) || 0) / g * 1000) / 10;
}

/**
 * Netto-Rentabilität (Cents) = Rohertrag − Fixkosten. S185 Cut 5.
 * fixCostsCents == null (keine Fixkosten hinterlegt) → null → KPI zeigt „—"
 * (nie Blocker, nur Rohertrag). Rohertrag kann negativ werden — gewollt.
 */
export function nettoRentabilitaetCents(rohertragCents, fixCostsCents) {
  if (fixCostsCents == null) return null;
  return (Number(rohertragCents) || 0) - (Number(fixCostsCents) || 0);
}

/**
 * Prorata-Beitrag (Cents) einer monatlichen Fixkostenzeile über `overlapDays`
 * Tage. PARITÄT zur SQL-RPC stats_operating_costs (phase_142):
 *   round(monthly * 12 / 365 * overlap_days). overlap ≤ 0 → 0.
 */
export function prorateMonthlyCents(monthlyCents, overlapDays) {
  const d = Number(overlapDays) || 0;
  if (d <= 0) return 0;
  return Math.round((Number(monthlyCents) || 0) * 12 / 365 * d);
}

/** Median einer Zahlenreihe (leer → 0). */
export function median(nums) {
  const xs = (Array.isArray(nums) ? nums : []).map(Number).filter(n => isFinite(n)).sort((a, b) => a - b);
  if (!xs.length) return 0;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
}

/**
 * Menu-Engineering-Klasse aus Menge × Deckungsbeitrag relativ zum Median:
 *   renner   = viel verkauft + hohe Marge (Star)
 *   gewinner = wenig verkauft + hohe Marge (Schmuckstück)
 *   schläfer = viel verkauft + niedrige Marge (Arbeitspferd)
 *   penner   = wenig verkauft + niedrige Marge (Verlierer)
 */
export function classifyMenuItem(qty, dbCents, medQty, medDb) {
  const hiQty = (Number(qty) || 0) >= (Number(medQty) || 0);
  const hiDb = (Number(dbCents) || 0) >= (Number(medDb) || 0);
  if (hiQty && hiDb) return 'renner';
  if (!hiQty && hiDb) return 'gewinner';
  if (hiQty && !hiDb) return 'schläfer';
  return 'penner';
}

/** 3a-Items → [{…, klasse}] mit Median-basierter Klassifizierung (pure). */
export function buildMenuEngineering(items) {
  const list = Array.isArray(items) ? items : [];
  const medQty = median(list.map(i => Number(i.qty) || 0));
  const medDb = median(list.map(i => Number(i.db_cents) || 0));
  return list.map(i => ({
    menu_item_id: i.menu_item_id,
    name: i.name,
    qty: Number(i.qty) || 0,
    db_cents: Number(i.db_cents) || 0,
    klasse: classifyMenuItem(i.qty, i.db_cents, medQty, medDb)
  }));
}

/** Ampel-Band eines Marge-Items: unvollständige/fehlende Rezeptdaten → 'unknown'. */
export function marginBand(item, targetPct = 30) {
  const incomplete = !!(item && (item.missing_data || item.has_recipe === false));
  return ampelFor({ pct: item ? item.food_cost_pct : null, missingData: incomplete, targetPct });
}

/* -------------------------------------------------------------------------- */
/* Render-Helfer (inline-HTML, Dark-Mode-Token-Farben)                         */
/* -------------------------------------------------------------------------- */

const ME_BADGE = Object.freeze({
  renner:   { label: 'Renner',   color: '#2f6b3a' },
  gewinner: { label: 'Gewinner', color: '#3a6ea5' },
  'schläfer': { label: 'Schläfer', color: '#d9a227' },
  penner:   { label: 'Penner',   color: '#b94a48' }
});

function _fmtPct(n) {
  if (n == null || !isFinite(Number(n))) return '—';
  return Number(n).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %';
}

/** Bestandswert (EUR-Zahl) → Cents-Format. stock_value_totals liefert €-Numerik. */
function _eurToCents(eur) { return Math.round((Number(eur) || 0) * 100); }

function _kpiCard(label, value, sub) {
  return `
    <div class="stat">
      <div class="stat-label">${htmlEscape(label)}</div>
      <div class="stat-value">${value}</div>
      <div class="stat-sub">${sub || '&nbsp;'}</div>
    </div>`;
}

function _marginRanking(margins) {
  const items = (margins && Array.isArray(margins.items)) ? margins.items : [];
  if (!items.length) return `<p class="hint">Keine verkauften Artikel im Zeitraum.</p>`;
  // S217 Cut 3 — ganze Liste (Server cappt nicht), Top 5 + „alle anzeigen".
  const li = items.map((i, idx) => {
      const band = marginBand(i);
      const dot = `<span title="Food-Cost-Ampel" style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${ampelColor(band)};flex:0 0 auto;"></span>`;
      const fc = (i.food_cost_pct == null || i.has_recipe === false) ? '—' : _fmtPct(i.food_cost_pct);
      return `<li style="margin:5px 0;display:flex;align-items:baseline;gap:10px;">
        <span style="flex:0 0 18px;color:var(--ink-muted);font-variant-numeric:tabular-nums;">${idx + 1}.</span>
        ${dot}
        <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${htmlEscape(i.name)}</span>
        <span style="flex:0 0 auto;white-space:nowrap;color:var(--ink-soft);font-variant-numeric:tabular-nums;">${i.qty}× · DB ${fmtCents(i.db_cents)} · FC ${fc}</span>
      </li>`;
    }).join('');
  return collapsibleRankHtml('ww-margins', li, items.length);
}

function _purchasingBars(purch) {
  const rows = (purch && Array.isArray(purch.suppliers)) ? purch.suppliers : [];
  if (!rows.length) return `<p class="hint">Kein Wareneingang im Zeitraum.</p>`;
  const max = Math.max(...rows.map(r => Number(r.spend_cents) || 0), 1);
  // S217 Cut 3-Nachzug — Top 5 + „alle anzeigen" (Balken-Liste). Summe bleibt außerhalb
  // des Collapse-Containers, damit sie nicht mit weggeklappt wird.
  const barsHtml = rows.map(r => {
    const pct = Math.round((Number(r.spend_cents) || 0) / max * 100);
    return `<div style="display:flex;align-items:center;gap:10px;margin:6px 0;">
      <span style="min-width:120px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${htmlEscape(r.name)}</span>
      <span style="flex:1;height:14px;background:var(--bg-soft);border-radius:7px;overflow:hidden;">
        <span style="display:block;height:100%;width:${pct}%;background:var(--green);"></span>
      </span>
      <span style="min-width:90px;text-align:right;font-variant-numeric:tabular-nums;">${fmtCents(r.spend_cents)}</span>
    </div>`;
  }).join('');
  return collapsibleRowsHtml('ww-purchasing', barsHtml, rows.length)
    + `<div class="stat-sub" style="margin-top:10px;">Gesamt-Wareneingang: <strong>${fmtCents(purch.total_spend_cents)}</strong> · ${purch.po_count || 0} Bestellung(en)</div>`;
}

function _topList(rows, valueFn, emptyHint, key) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return `<p class="hint">${htmlEscape(emptyHint)}</p>`;
  // S217 Cut 3 — ganze Liste, Top 5 + „alle anzeigen" (gemeinsames Grundmuster).
  const li = list.map((r, idx) => `<li style="margin:5px 0;display:flex;align-items:baseline;gap:10px;">
      <span style="flex:0 0 18px;color:var(--ink-muted);font-variant-numeric:tabular-nums;">${idx + 1}.</span>
      <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${htmlEscape(r.name)}</span>
      <span style="flex:0 0 auto;white-space:nowrap;color:var(--ink-soft);font-variant-numeric:tabular-nums;">${valueFn(r)}</span>
    </li>`).join('');
  return collapsibleRankHtml(key, li, list.length);
}

function _fmtBaseQty(q) {
  return (Number(q) || 0).toLocaleString('de-DE', { maximumFractionDigits: 1 });
}

function _menuEngineering(margins) {
  const items = (margins && Array.isArray(margins.items)) ? margins.items : [];
  if (!items.length) return `<p class="hint">Keine Daten.</p>`;
  // S217 Cut 3 — ganze Liste (kein slice), Top 5 + „alle anzeigen".
  const rows = buildMenuEngineering(items)
    .sort((a, b) => b.qty - a.qty || b.db_cents - a.db_cents);
  const li = rows.map(r => {
      const b = ME_BADGE[r.klasse] || ME_BADGE.penner;
      return `<li style="margin:5px 0;display:flex;align-items:baseline;gap:10px;">
        <span style="flex:0 0 auto;font-size:.72rem;color:#fff;background:${b.color};padding:1px 7px;border-radius:9px;">${b.label}</span>
        <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${htmlEscape(r.name)}</span>
        <span style="flex:0 0 auto;white-space:nowrap;color:var(--ink-soft);font-variant-numeric:tabular-nums;">${r.qty}× · DB ${fmtCents(r.db_cents)}</span>
      </li>`;
    }).join('');
  return collapsibleRankHtml('ww-menueng', li, rows.length);
}

function _mhdRisk(lots, stockOv) {
  const lotList = Array.isArray(lots) ? lots : [];
  const names = {};
  (Array.isArray(stockOv) ? stockOv : []).forEach(s => { names[s.ingredient_id] = s.name; });
  const risk = lotList.filter(l => l.has_near || l.has_expired);
  if (!risk.length) return `<p class="hint">Keine Chargen mit MHD-Risiko.</p>`;
  const nNear = risk.filter(l => l.has_near && !l.has_expired).length;
  const nExp = risk.filter(l => l.has_expired).length;
  // S217 Cut 3 — ganze Risiko-Liste, Top 5 + „alle anzeigen".
  const li = risk.map(l => `<li style="margin:5px 0;display:flex;align-items:baseline;gap:10px;">
        <span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${l.has_expired ? '#b94a48' : '#d9a227'};flex:0 0 auto;"></span>
        <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${htmlEscape(names[l.ingredient_id] || l.ingredient_id)}</span>
        <span style="flex:0 0 auto;white-space:nowrap;color:var(--ink-soft);">${l.has_expired ? 'abgelaufen' : 'bald fällig'} · ${l.active_lots} Charge(n)</span>
      </li>`).join('');
  return `
    <div class="stat-sub" style="margin:0 0 8px;"><strong>${nExp}</strong> abgelaufen · <strong>${nNear}</strong> bald fällig</div>
    ${collapsibleRankHtml('ww-mhd', li, risk.length)}`;
}

/* -------------------------------------------------------------------------- */
/* Daten laden (RPCs) + Render                                                 */
/* -------------------------------------------------------------------------- */

function _supa() { return (window.App && window.App.supabase) || null; }

async function _rpc(name, params) {
  const supa = _supa();
  if (!supa) throw new Error('Keine Verbindung.');
  const { data, error } = await supa.rpc(name, params || {});
  if (error) throw error;
  return data;
}

/** Degradierter Sektions-Platzhalter, wenn ein einzelner RPC fehlschlug. */
function _sectionError(label) {
  return `<p class="hint" style="text-align:center;padding:18px;">${htmlEscape(label)} derzeit nicht verfügbar.</p>`;
}

/* -------------------------------------------------------------------------- */
/* S185 Cut 5 — Fixkosten-Kontext (für partielle, non-destruktive Re-Renders)  */
/* -------------------------------------------------------------------------- */

// Modul-lokaler Kontext: erlaubt KPI- + Fixkosten-Block separat neu zu rendern,
// ohne den ganzen Tab (oder die teuren Waren-RPCs) erneut zu ziehen.
const _wwCtx = {
  range: null,       // { from, to }
  data: null,        // letztes Waren-RPC-Bundle (für KPI-Re-Render)
  costs: [],         // operating_costs-Zeilen (Stammdaten)
  opCosts: null,     // stats_operating_costs-Ergebnis (prorata) für den Zeitraum
  canEdit: false,    // owner/admin/manager (operatingcosts.edit)
  editId: null,      // aktuell inline editierte Zeile
  adding: false      // Neuanlage-Form offen?
};

function _opCostsApi() {
  return (window.App && window.App.storage && window.App.storage.operatingCosts) || null;
}

async function _listCosts() {
  const api = _opCostsApi();
  if (!api) return [];
  try { return await api.list(); }
  catch (e) { console.warn('[stats-ww] operatingCosts.list:', e); return []; }
}

/** Darf der aktuelle Operator Fixkosten editieren? (owner/admin/manager; RLS hart). */
function _canEditCosts() {
  const OR = (typeof window !== 'undefined') ? window.OperatorRoles : null;
  if (!OR) return true; // ohne Rollen-Modul nicht künstlich sperren — Server-RLS bleibt autoritativ
  const opApi = window.App && window.App.operator;
  const op = (opApi && typeof opApi.getOperator === 'function') ? opApi.getOperator() : null;
  return OR.operatorCan(op, 'operatingcosts.edit');
}

function _operatorName() {
  const opApi = window.App && window.App.operator;
  const op = (opApi && typeof opApi.getOperator === 'function') ? opApi.getOperator() : null;
  return (op && (op.name || op.initials)) || null;
}

/**
 * Lädt + rendert das Warenwirtschaft-Dashboard in `el`. range = {from,to}.
 * Non-destruktiv (ersetzt nur den Inhalt von `el`). allSettled → Sektions-Degradation.
 */
export async function renderWarenwirtschaftInto(el, range) {
  if (!el) return;
  if (!range || !range.from || !range.to) {
    el.innerHTML = `<p class="hint" style="text-align:center;padding:24px;">Zeitraum wählen.</p>`;
    return;
  }
  el.innerHTML = `<p class="hint" style="text-align:center;padding:24px;">Lade Kennzahlen…</p>`;
  const { from, to } = range;
  const calls = [
    ['stats_item_margins', { p_from: from, p_to: to }],
    ['stats_revenue_overview', { p_from: from, p_to: to }],
    ['stats_purchasing', { p_from: from, p_to: to }],
    ['stats_stock_flows', { p_from: from, p_to: to }],
    ['stock_value_totals', {}],
    ['lots_summary', {}],
    ['stock_overview', {}],
    ['stats_operating_costs', { p_from: from, p_to: to }]  // S185 Cut 5 — Fixkosten prorata
  ];
  try {
    const [settled, costs] = await Promise.all([
      Promise.allSettled(calls.map(([n, p]) => _rpc(n, p))),
      _listCosts()
    ]);
    settled.forEach((s, i) => { if (s.status === 'rejected') console.error('[stats-ww] rpc', calls[i][0], s.reason); });
    const val = (i) => (settled[i].status === 'fulfilled' ? settled[i].value : null);
    _wwCtx.range = { from, to };
    _wwCtx.data = {
      margins: val(0),
      rev: val(1),
      purch: val(2),
      flows: val(3),
      stockVal: Array.isArray(val(4)) ? val(4)[0] : val(4),
      lots: val(5),
      stockOv: val(6)
    };
    _wwCtx.opCosts = val(7);
    _wwCtx.costs = Array.isArray(costs) ? costs : [];
    _wwCtx.canEdit = _canEditCosts();
    _wwCtx.editId = null;
    _wwCtx.adding = false;
    el.innerHTML = _renderDashboard(_wwCtx.data);
    // S189 — Food-Cost je Gericht ist jetzt Teil der Warenwirtschaft (aus „Ware" gezogen);
    // eigener async-Load in den Host #ww-foodcost, flag-gated wie der Report selbst.
    if (_erpFlag() && typeof window.renderFoodCostInto === 'function') window.renderFoodCostInto('ww-foodcost');
  } catch (e) {
    console.error('[stats-ww] load:', e);
    el.innerHTML = `<p class="hint" style="text-align:center;padding:24px;">Kennzahlen konnten nicht geladen werden: ${htmlEscape(e.message || String(e))}</p>`;
  }
}

/** Fixkosten-Summe (Cents) für den aktiven Zeitraum, oder null wenn keine
 *  Fixkosten hinterlegt sind (→ Netto-Rentabilität zeigt „—"). */
function _fixCostsCents() {
  if (!Array.isArray(_wwCtx.costs) || _wwCtx.costs.length === 0) return null;
  return Number(_wwCtx.opCosts && _wwCtx.opCosts.total_cents) || 0;
}

/** KPI-Strip (separat re-render-bar: #ww-kpis). */
function _renderKpis({ margins, rev, flows, stockVal }) {
  const goodsCost = margins ? Number(margins.total_goods_cost_cents) || 0 : null;
  const salesGross = rev ? Number(rev.sales_gross_cents) || 0 : null;
  const waste = flows ? Number(flows.waste_total_cents) || 0 : null;

  const wek = (margins && rev) ? wareneinsatzQuotePct(goodsCost, salesGross) : null;
  const roh = (margins && rev) ? rohertragCents(salesGross, goodsCost) : null;
  const schwQ = (flows && margins) ? schwundQuotePct(waste, goodsCost) : null;

  // Netto-Rentabilität neben Rohertrag — nur wenn Fixkosten hinterlegt; sonst „—".
  const fixCents = _fixCostsCents();
  const netto = (roh != null) ? nettoRentabilitaetCents(roh, fixCents) : null;
  const nettoSub = (roh == null)
    ? 'Daten fehlen'
    : (fixCents == null ? 'keine Fixkosten hinterlegt' : `Rohertrag − Fixkosten ${fmtCents(fixCents)}`);

  return `
    <div class="stats">
      ${_kpiCard('Wareneinsatz-Quote', (margins && rev) ? _fmtPct(wek) : '—', (margins && rev) ? `Wareneinsatz ${fmtCents(goodsCost)}` : 'Daten fehlen')}
      ${_kpiCard('Rohertrag', (margins && rev) ? fmtCents(roh) : '—', (margins && rev) ? `Umsatz ${fmtCents(salesGross)}` : 'Daten fehlen')}
      ${_kpiCard('Netto-Rentabilität', netto != null ? fmtCents(netto) : '—', nettoSub)}
      ${_kpiCard('Schwund', flows ? fmtCents(waste) : '—', flows ? `Quote ${schwQ == null ? '—' : _fmtPct(schwQ)} vom Wareneinsatz` : 'Daten fehlen')}
      ${_kpiCard('Bestandswert', stockVal ? fmtCents(_eurToCents(stockVal.total_yield)) : '—', stockVal ? `${stockVal.valued_count || 0} bewertet · ${stockVal.unvalued_count || 0} ohne Preis` : 'Daten fehlen')}
    </div>`;
}

function _erpFlag() {
  return !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb);
}

function _renderDashboard(data) {
  const { margins, purch, flows, lots, stockOv } = data || {};
  return `
    <div id="ww-kpis">${_renderKpis(data)}</div>
    <div class="card" id="ww-fixkosten" style="margin-top:16px;">${_fixkostenInner()}</div>
    <div class="card" style="margin-top:16px;">
      <h3 style="margin:0 0 10px;">Deckungsbeitrag je Artikel</h3>
      ${margins ? _marginRanking(margins) : _sectionError('Deckungsbeitrag')}
    </div>
    <div class="stats-duo" style="margin-top:16px;">
      <div class="card">
        <h3 style="margin:0 0 8px;">Einkauf je Lieferant</h3>
        ${purch ? _purchasingBars(purch) : _sectionError('Einkauf')}
      </div>
      <div class="card">
        <h3 style="margin:0 0 8px;">MHD-Risiko</h3>
        ${(lots && stockOv) ? _mhdRisk(lots, stockOv) : _sectionError('MHD-Risiko')}
      </div>
    </div>
    <div class="stats-duo" style="margin-top:16px;">
      <div class="card">
        <h3 style="margin:0 0 8px;">Schwund (Top)</h3>
        ${flows ? _topList(flows.waste_top, r => `${_fmtBaseQty(r.qty_base)} · ${fmtCents(r.value_cents)}`, 'Kein Schwund im Zeitraum.', 'ww-waste') : _sectionError('Schwund')}
      </div>
      <div class="card">
        <h3 style="margin:0 0 8px;">Meistverbrauchte Zutaten</h3>
        ${flows ? _topList(flows.consumption_top, r => `${_fmtBaseQty(r.qty_base)} Basis`, 'Kein Verbrauch im Zeitraum.', 'ww-consumption') : _sectionError('Verbrauch')}
      </div>
    </div>
    <div class="card" style="margin-top:16px;">
      <h3 style="margin:0 0 10px;">Renner & Penner (Menge × Deckungsbeitrag)</h3>
      ${margins ? _menuEngineering(margins) : _sectionError('Renner & Penner')}
    </div>
    ${_erpFlag() ? `<div class="card" style="margin-top:16px;">
      <h3 style="margin:0 0 4px;">Food-Cost je Gericht</h3>
      <p class="hint" style="margin:0 0 10px;">Wareneinsatz · Food-Cost % · Marge je verkaufbarem Gericht (aus „Ware" hierher gezogen).</p>
      <div id="ww-foodcost"></div>
    </div>` : ''}`;
}

/* -------------------------------------------------------------------------- */
/* S185 Cut 5 — Fixkosten-Editier-Block (inline, non-destruktiv)               */
/* -------------------------------------------------------------------------- */

const _FIX_CATEGORIES = Object.freeze(['Miete', 'Energie', 'Versicherung', 'Personal (extern)', 'Marketing', 'Sonstiges']);

/** 'YYYY-MM-DD' → 'DD.MM.YYYY' (string-basiert, kein TZ-Shift). */
function _fmtDate(d) {
  if (!d) return '';
  const p = String(d).slice(0, 10).split('-');
  return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : String(d);
}

/** EUR-Eingabe (number-Input, Dezimalpunkt) → Cents (ganzzahlig). NaN bei leer/ungültig.
 *  (Nicht zu verwechseln mit _eurToCents oben — das nimmt eine fertige €-Zahl.) */
function _fixEurToCents(eur) {
  const n = parseFloat(String(eur == null ? '' : eur).replace(',', '.'));
  return isFinite(n) ? Math.round(n * 100) : NaN;
}
/** Cents → EUR-Zahl-String fürs number-Input (Punkt-Dezimal, 2 NK). */
function _centsToEurInput(cents) {
  return ((Number(cents) || 0) / 100).toFixed(2);
}

/** Innerer HTML des Fixkosten-Blocks (#ww-fixkosten) — separat re-render-bar. */
function _fixkostenInner() {
  const rows = Array.isArray(_wwCtx.costs) ? _wwCtx.costs : [];
  const canEdit = !!_wwCtx.canEdit;
  const total = _fixCostsCents();

  const head = `
    <div style="display:flex;align-items:baseline;justify-content:space-between;gap:10px;flex-wrap:wrap;margin:0 0 10px;">
      <h3 style="margin:0;">Fixkosten (monatlich)</h3>
      ${total != null
        ? `<span class="stat-sub" style="color:var(--ink-soft);">Σ im Zeitraum: <strong>${fmtCents(total)}</strong></span>`
        : `<span class="stat-sub" style="color:var(--ink-muted);">noch keine hinterlegt</span>`}
    </div>`;

  // Liste / Leerzustand
  let list;
  if (!rows.length) {
    list = `<p class="hint" style="margin:0 0 4px;">Keine Fixkosten hinterlegt — die Netto-Rentabilität bleibt ausgeblendet (nur Rohertrag).</p>`;
  } else {
    list = `<ol class="stats-rank" style="margin:0;padding:0;list-style:none;">${
      rows.map(r => (_wwCtx.editId === r.id) ? _fixFormRow(r) : _fixViewRow(r, canEdit)).join('')
    }</ol>`;
  }

  // Neuanlage-Form / „Hinzufügen"-Button (nur owner/manager)
  let addArea = '';
  if (canEdit) {
    addArea = _wwCtx.adding
      ? _fixFormRow(null)
      : `<div style="margin-top:10px;">
           <button class="btn btn-secondary btn-sm" onclick="wwFixAdd()">+ Fixkosten hinzufügen</button>
         </div>`;
  } else {
    addArea = `<p class="hint" style="margin-top:8px;color:var(--ink-muted);">Nur Inhaber/Manager können Fixkosten bearbeiten.</p>`;
  }

  return head + list + addArea;
}

/** Anzeige-Zeile einer Fixkostenposition. */
function _fixViewRow(r, canEdit) {
  const bis = r.validTo ? _fmtDate(r.validTo) : 'offen';
  const note = r.note ? ` · <span style="color:var(--ink-muted);">${htmlEscape(r.note)}</span>` : '';
  const actions = canEdit
    ? `<span style="flex:0 0 auto;display:inline-flex;gap:6px;">
         <button class="btn btn-ghost btn-sm" title="Bearbeiten" onclick="wwFixEdit('${htmlEscape(r.id)}')">✎</button>
         <button class="btn btn-ghost btn-sm" title="Löschen" onclick="wwFixDelete('${htmlEscape(r.id)}')">🗑</button>
       </span>`
    : '';
  return `
    <li style="margin:6px 0;display:flex;align-items:baseline;gap:10px;">
      <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
        <strong>${htmlEscape(r.category || '—')}</strong>
        <span style="color:var(--ink-soft);"> · ${_fmtDate(r.validFrom)} – ${bis}</span>${note}
      </span>
      <span style="flex:0 0 auto;color:var(--ink-soft);font-variant-numeric:tabular-nums;">${fmtCents(r.monthlyAmountCents)}/Mt.</span>
      ${actions}
    </li>`;
}

/**
 * Inline-Formularzeile (Neuanlage r=null oder Bearbeiten). Inputs sind
 * UNGESTEUERT — kein Re-Render bei Tastendruck (Memory gg-non-destructive-render);
 * Werte werden erst beim Speichern aus dem DOM gelesen.
 */
function _fixFormRow(r) {
  const isEdit = !!(r && r.id);
  const idAttr = isEdit ? htmlEscape(r.id) : '';
  const cat = isEdit ? htmlEscape(r.category || '') : '';
  const amt = isEdit ? _centsToEurInput(r.monthlyAmountCents) : '';
  const vf = isEdit ? String(r.validFrom || '').slice(0, 10) : '';
  const vt = isEdit ? String(r.validTo || '').slice(0, 10) : '';
  const note = isEdit ? htmlEscape(r.note || '') : '';
  const dl = _FIX_CATEGORIES.map(c => `<option value="${htmlEscape(c)}"></option>`).join('');
  return `
    <li style="margin:8px 0;padding:12px;border:1px solid var(--border);border-radius:8px;background:var(--bg-soft);list-style:none;"
        data-fix-form="${idAttr}">
      <input type="hidden" id="wwfix-id" value="${idAttr}">
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
        <label style="display:flex;flex-direction:column;gap:3px;font-size:.82rem;color:var(--ink-soft);">
          Kategorie
          <input id="wwfix-cat" list="wwfix-cat-list" value="${cat}" placeholder="z. B. Miete"
                 style="padding:6px 8px;background:var(--bg);color:var(--ink);border:1px solid var(--border);border-radius:6px;">
          <datalist id="wwfix-cat-list">${dl}</datalist>
        </label>
        <label style="display:flex;flex-direction:column;gap:3px;font-size:.82rem;color:var(--ink-soft);">
          Monatsbetrag (€)
          <input id="wwfix-amt" type="number" step="0.01" min="0" value="${amt}" placeholder="0,00"
                 style="padding:6px 8px;background:var(--bg);color:var(--ink);border:1px solid var(--border);border-radius:6px;">
        </label>
        <label style="display:flex;flex-direction:column;gap:3px;font-size:.82rem;color:var(--ink-soft);">
          Gültig ab
          <input id="wwfix-from" type="date" value="${htmlEscape(vf)}"
                 style="padding:6px 8px;background:var(--bg);color:var(--ink);border:1px solid var(--border);border-radius:6px;">
        </label>
        <label style="display:flex;flex-direction:column;gap:3px;font-size:.82rem;color:var(--ink-soft);">
          Gültig bis <span style="color:var(--ink-muted);">(optional)</span>
          <input id="wwfix-to" type="date" value="${htmlEscape(vt)}"
                 style="padding:6px 8px;background:var(--bg);color:var(--ink);border:1px solid var(--border);border-radius:6px;">
        </label>
      </div>
      <label style="display:flex;flex-direction:column;gap:3px;font-size:.82rem;color:var(--ink-soft);margin-top:8px;">
        Notiz <span style="color:var(--ink-muted);">(optional)</span>
        <input id="wwfix-note" value="${note}" placeholder="frei"
               style="padding:6px 8px;background:var(--bg);color:var(--ink);border:1px solid var(--border);border-radius:6px;">
      </label>
      <div style="display:flex;gap:8px;margin-top:10px;">
        <button class="btn btn-primary btn-sm" onclick="wwFixSave()">${isEdit ? 'Speichern' : 'Anlegen'}</button>
        <button class="btn btn-ghost btn-sm" onclick="wwFixCancel()">Abbrechen</button>
      </div>
    </li>`;
}

/** Nur den Fixkosten-Block neu rendern (Liste/Form) — kein Tab-Reset. */
function _rerenderFixblock() {
  const el = document.getElementById('ww-fixkosten');
  if (el) el.innerHTML = _fixkostenInner();
}
/** Nur den KPI-Strip neu rendern (Netto-Rentabilität nach Fixkosten-Änderung). */
function _rerenderKpis() {
  const el = document.getElementById('ww-kpis');
  if (el && _wwCtx.data) el.innerHTML = _renderKpis(_wwCtx.data);
}

/** Fixkosten + prorata-Summe neu aus DB ziehen (nach Mutation). */
async function _reloadCosts() {
  const r = _wwCtx.range || {};
  try {
    const [opCosts, costs] = await Promise.all([
      (r.from && r.to) ? _rpc('stats_operating_costs', { p_from: r.from, p_to: r.to }).catch(() => _wwCtx.opCosts) : Promise.resolve(_wwCtx.opCosts),
      _listCosts()
    ]);
    _wwCtx.opCosts = opCosts;
    _wwCtx.costs = Array.isArray(costs) ? costs : [];
  } catch (e) {
    console.warn('[stats-ww] _reloadCosts:', e);
  }
}

/* ----- Inline-Handler (window-registriert, CSP: func() ohne window.-Prefix) -- */

export function wwFixAdd() {
  if (!_wwCtx.canEdit) return;
  _wwCtx.adding = true;
  _wwCtx.editId = null;
  _rerenderFixblock();
}

export function wwFixEdit(id) {
  if (!_wwCtx.canEdit) return;
  _wwCtx.editId = id;
  _wwCtx.adding = false;
  _rerenderFixblock();
}

export function wwFixCancel() {
  _wwCtx.adding = false;
  _wwCtx.editId = null;
  _rerenderFixblock();
}

export async function wwFixSave() {
  if (!_wwCtx.canEdit) return;
  const $ = (id) => document.getElementById(id);
  const id = ($('wwfix-id') && $('wwfix-id').value) || null;
  const category = ($('wwfix-cat') && $('wwfix-cat').value || '').trim();
  const cents = _fixEurToCents($('wwfix-amt') && $('wwfix-amt').value);
  const validFrom = ($('wwfix-from') && $('wwfix-from').value) || '';
  const validTo = ($('wwfix-to') && $('wwfix-to').value) || '';
  const note = ($('wwfix-note') && $('wwfix-note').value || '').trim();

  if (!category) { showToast('Bitte eine Kategorie angeben.', 'error'); return; }
  if (!isFinite(cents) || cents < 0) { showToast('Bitte einen gültigen Monatsbetrag angeben.', 'error'); return; }
  if (!validFrom) { showToast('Bitte „Gültig ab" angeben.', 'error'); return; }
  if (validTo && validTo < validFrom) { showToast('„Gültig bis" darf nicht vor „Gültig ab" liegen.', 'error'); return; }

  const api = _opCostsApi();
  if (!api) { showToast('Speichern derzeit nicht möglich.', 'error'); return; }
  try {
    await api.upsert({
      id: id || undefined,
      category,
      monthlyAmountCents: cents,
      validFrom,
      validTo: validTo || null,
      note: note || null,
      createdBy: id ? undefined : _operatorName()
    });
    _wwCtx.adding = false;
    _wwCtx.editId = null;
    await _reloadCosts();
    _rerenderFixblock();
    _rerenderKpis();
    showToast(id ? 'Fixkosten aktualisiert.' : 'Fixkosten angelegt.', 'success');
  } catch (e) {
    console.error('[stats-ww] wwFixSave:', e);
    showToast('Speichern fehlgeschlagen: ' + (e && e.message ? e.message : e), 'error');
  }
}

export async function wwFixDelete(id) {
  if (!_wwCtx.canEdit || !id) return;
  if (!(await window.styledConfirm({ title: 'Diese Fixkostenposition löschen?', danger: true, confirmLabel: 'Löschen' })).confirmed) return;
  const api = _opCostsApi();
  if (!api) return;
  try {
    await api.remove(id);
    if (_wwCtx.editId === id) _wwCtx.editId = null;
    await _reloadCosts();
    _rerenderFixblock();
    _rerenderKpis();
    showToast('Fixkosten gelöscht.', 'success');
  } catch (e) {
    console.error('[stats-ww] wwFixDelete:', e);
    showToast('Löschen fehlgeschlagen: ' + (e && e.message ? e.message : e), 'error');
  }
}

/* -------------------------------------------------------------------------- */
/* window-Bridge (CSP: inline func())                                          */
/* -------------------------------------------------------------------------- */
if (typeof window !== 'undefined') {
  Object.assign(window, { wwFixAdd, wwFixEdit, wwFixCancel, wwFixSave, wwFixDelete });
}
